/**
 * PrintContext - Single Source of Truth for Print Area Information
 *
 * This module provides a centralized data structure for all print area
 * calculations. All components that need size, coordinate, or DPI information
 * should use this context instead of calculating values independently.
 *
 * Used by:
 * - Frontend: Canvas editor, design scaling, placement preview
 * - Backend: Canvas data generation, mockup rendering
 */

import type {
  CarryOverAnchor,
  ProductClass,
} from '../design/carryOverDefaults.js';
import type { SizeTier } from '../placement/sizeTier.js';

/**
 * PrintContext - All information about the current print area
 *
 * Contains both raw pixel/mm values and derived values (pxPerMm, cm).
 * The derived values are calculated once when the context is created,
 * ensuring consistency across all consumers.
 */
export interface PrintContext {
  // ═══════════════════════════════════════════════════════════════════
  // PIXEL DIMENSIONS (for Canvas/Fabric.js)
  // ═══════════════════════════════════════════════════════════════════

  /** Print area width in pixels (canvas coordinates) */
  widthPx: number;
  /** Print area height in pixels (canvas coordinates) */
  heightPx: number;

  // ═══════════════════════════════════════════════════════════════════
  // PHYSICAL DIMENSIONS (for display and DPI calculation)
  // ═══════════════════════════════════════════════════════════════════

  /** Print area width in millimeters (physical) */
  widthMm: number;
  /** Print area height in millimeters (physical) */
  heightMm: number;

  // ═══════════════════════════════════════════════════════════════════
  // DERIVED VALUES (calculated, not manually set)
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Pixel Scale of the template the context was built from (pixels per mm
   * at the canvas resolution). An input, never derived from the rounded
   * pixel rectangle: the canvas JSON is stored divided by exactly this
   * value (R34).
   */
  pxPerMm: number;
  /** Print area width in centimeters (widthMm / 10) */
  widthCm: number;
  /** Print area height in centimeters (heightMm / 10) */
  heightCm: number;

  // ═══════════════════════════════════════════════════════════════════
  // POSITION IN CANVAS (for clipping and positioning)
  // ═══════════════════════════════════════════════════════════════════

  /** X offset from canvas left edge in pixels */
  offsetX: number;
  /** Y offset from canvas top edge in pixels */
  offsetY: number;

  // ═══════════════════════════════════════════════════════════════════
  // MOCKUP CONTEXT (parent canvas dimensions)
  // ═══════════════════════════════════════════════════════════════════

  /** Mockup/Canvas native width in pixels */
  mockupWidthPx: number;
  /** Mockup/Canvas native height in pixels */
  mockupHeightPx: number;

  // ═══════════════════════════════════════════════════════════════════
  // SIZE TIER (carry-over rule)
  // ═══════════════════════════════════════════════════════════════════

  /** Effective size tier of the print area, when known */
  sizeTier?: SizeTier;
  /**
   * Admin override of the tier, when the context was built from a live
   * catalogue row. Carried so a switch in the editor sees "fill this area"
   * the same way the product cards do; persisted contexts do not have it,
   * and they describe a source, where it has no effect.
   */
  sizeTierOverride?: SizeTier | null;

  // ═══════════════════════════════════════════════════════════════════
  // CARRY-OVER SETTINGS (product and area, from the live catalogue)
  // ═══════════════════════════════════════════════════════════════════

  /** The product this print area belongs to (rule 1 identity, snapshots) */
  productId?: string;
  /** Product class, when known */
  productClass?: ProductClass | null;
  /** Effective carry-over percent (area value, else product value) */
  carryOverPercent?: number | null;
  /** Effective vertical anchor; null means class default */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Top offset in mm for anchor TOP; null means the global default */
  carryOverTopOffsetMm?: number | null;
}

/**
 * Parameters for creating a PrintContext
 */
export interface CreatePrintContextParams {
  /** Print area width in pixels */
  widthPx: number;
  /** Print area height in pixels */
  heightPx: number;
  /** Print area width in millimeters */
  widthMm: number;
  /** Print area height in millimeters */
  heightMm: number;
  /** Pixel Scale of the template (pixels per mm); no default (R5) */
  pxPerMm: number;
  /** X offset from canvas left (optional, default: calculated from center) */
  offsetX?: number;
  /** Y offset from canvas top (optional, default: 0) */
  offsetY?: number;
  /** Mockup width in pixels (optional, default: widthPx) */
  mockupWidthPx?: number;
  /** Mockup height in pixels (optional, default: heightPx) */
  mockupHeightPx?: number;
  /** Effective size tier of the print area (optional) */
  sizeTier?: SizeTier;
  /** Admin override of the tier (optional) */
  sizeTierOverride?: SizeTier | null;
  /** The product the area belongs to (optional) */
  productId?: string;
  /** Product class (optional) */
  productClass?: ProductClass | null;
  /** Effective carry-over percent (optional) */
  carryOverPercent?: number | null;
  /** Effective vertical anchor (optional) */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Top offset in mm for anchor TOP (optional) */
  carryOverTopOffsetMm?: number | null;
}

/**
 * Create a PrintContext with automatic calculation of derived values
 *
 * The pixel rectangle comes from `templateRect` (one formula, R7) and the
 * Pixel Scale from the same template; this factory only adds the derived
 * centimetre values and defaults the mockup size and centring.
 *
 * @param params - The input parameters (pixel and mm dimensions)
 * @returns A complete PrintContext object with all derived values
 *
 * @example
 * ```typescript
 * const context = createPrintContext({
 *   widthPx: 500,
 *   heightPx: 600,
 *   widthMm: 380,
 *   heightMm: 460,
 *   pxPerMm: 1.316,
 *   offsetX: 50,
 *   offsetY: 100,
 *   mockupWidthPx: 600,
 *   mockupHeightPx: 800,
 * });
 * // context.widthCm = 38.0 (380 / 10)
 * ```
 */
export function createPrintContext(
  params: CreatePrintContextParams
): PrintContext {
  const {
    widthPx,
    heightPx,
    widthMm,
    heightMm,
    pxPerMm,
    offsetX,
    offsetY = 0,
    mockupWidthPx,
    mockupHeightPx,
    sizeTier,
    sizeTierOverride,
    productId,
    productClass,
    carryOverPercent,
    carryOverAnchor,
    carryOverTopOffsetMm,
  } = params;

  // Calculate cm values
  const widthCm = widthMm / 10;
  const heightCm = heightMm / 10;

  // Use provided mockup dimensions or fall back to print area dimensions
  const actualMockupWidth = mockupWidthPx ?? widthPx;
  const actualMockupHeight = mockupHeightPx ?? heightPx;

  // Calculate offsetX if not provided (center the print area)
  const actualOffsetX = offsetX ?? (actualMockupWidth - widthPx) / 2;

  return {
    // Raw pixel dimensions
    widthPx,
    heightPx,

    // Physical dimensions
    widthMm,
    heightMm,

    // Derived values
    pxPerMm,
    widthCm,
    heightCm,

    // Position
    offsetX: actualOffsetX,
    offsetY,

    // Mockup dimensions
    mockupWidthPx: actualMockupWidth,
    mockupHeightPx: actualMockupHeight,

    // Size tier (only when known)
    ...(sizeTier !== undefined && { sizeTier }),
    ...(sizeTierOverride !== undefined && { sizeTierOverride }),

    // Carry-over settings (only when known)
    ...(productId !== undefined && { productId }),
    ...(productClass !== undefined && { productClass }),
    ...(carryOverPercent !== undefined && { carryOverPercent }),
    ...(carryOverAnchor !== undefined && { carryOverAnchor }),
    ...(carryOverTopOffsetMm !== undefined && { carryOverTopOffsetMm }),
  };
}
